/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { TableUtility } from '@utility/dynamic-table';
import {
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Button } from 'primereact/button';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { useAuth } from '../../Hooks/useAuth';
import { MODULE_NAMES } from '../../Constants/modules';
import UseAxios from '../../Api/http/http';
import { getEmployeeById } from '../../Api/request/employeeMaster';
import {
  getHomeDashboardData,
  getHomeDashboardExportData,
  getHomeDashboardFilters,
} from '../../Api/request/homeDashboard';
import exportToExcel from '../../Utils/exportToExcel';
import { useSnackbar } from '../../Components/Snackbar/snackBarProvider';
import Chart from '../../Components/Chart/chart';
import Loader from '../../Components/Loader';
import downloadIcon from '../../Assets/images/svg/dar_collection_dashboard_download.svg';
import expandAllIcon from '../../Assets/images/svg/dar_collection_dashboard_expand_all.svg';
import collapseAllIcon from '../../Assets/images/svg/dar_collection_dashboard_collapse_all.svg';
import noDataSearchIcon from '../../Assets/images/svg/no-data-search-icon.svg';
import './home.scss';

const FILTER_FIELDS = [
  { key: 'financialYear', label: 'Financial Year', type: 'select' },
  { key: 'quarterId', label: 'Quarter', type: 'select' },
  { key: 'seniorManagerEmpNo', label: 'Delivery Manager / Senior Manager', type: 'select' },
  { key: 'architectEmpNo', label: 'Architect', type: 'select' },
  { key: 'pmEmpId', label: 'PM', type: 'select' },
];

const SINGLE_SELECT_FILTERS = new Set([
  'financialYear',
  'seniorManagerEmpNo',
  'architectEmpNo',
  'pmEmpId',
]);

const HOME_PERMISSION_MODULES = [
  MODULE_NAMES.HOME_DASHBOARD,
  MODULE_NAMES.HOME_DASHBOARD_ACCOUNT_BASED_REPORT,
  MODULE_NAMES.HOME_DASHBOARD_ARCHITECT_REPORT,
  MODULE_NAMES.HOME_DASHBOARD_PM_REPORT,
  MODULE_NAMES.HOME_DASHBOARD_ACCOUNT_BASED_FILTER,
  MODULE_NAMES.HOME_DASHBOARD_ARCHITECT_FILTER,
  MODULE_NAMES.HOME_DASHBOARD_PM_FILTER,
  MODULE_NAMES.HOME_DASHBOARD_ARCHITECT_TECHNOLOGY,
  MODULE_NAMES.HOME_DASHBOARD_ARCHITECT_EFFORT_DEVIATION,
  MODULE_NAMES.HOME_DASHBOARD_ARCHITECT_UTILIZATION,
  MODULE_NAMES.HOME_DASHBOARD_ARCHITECT_RU_UNIT,
  MODULE_NAMES.HOME_DASHBOARD_PM_CSAT,
  MODULE_NAMES.HOME_DASHBOARD_PM_QUALITY_AUDIT,
  MODULE_NAMES.HOME_DASHBOARD_PM_PMO_AUDIT,
  MODULE_NAMES.HOME_DASHBOARD_PM_RU_UNIT,
];

const filterHomeColumnsByPermission = (columns, visibility = {}) => columns.reduce((filtered, column) => {
  const permissionKeyByField = {
    revenueUnit: 'revenueUnit',
    technologyAuditRating: 'technologyAudit',
    effortDeviation: 'effortDeviation',
    utilization: 'utilization',
    averageCsat: 'averageCsat',
    csatReceived: 'csatReceived',
    qualityAuditRating: 'qualityAudit',
    pmoAuditRating: 'pmoAudit',
  };
  const permissionKey = permissionKeyByField[column.field];
  if (permissionKey && visibility[permissionKey] === false) return filtered;

  const nextColumn = column.children
    ? { ...column, children: filterHomeColumnsByPermission(column.children, visibility) }
    : column;
  if (nextColumn.children && nextColumn.children.length === 0) return filtered;
  return [...filtered, nextColumn];
}, []);

const hasReadPermission = (permissions = {}, moduleName) => (
  Array.isArray(permissions[moduleName])
  && permissions[moduleName].some((action) => String(action).toLowerCase() === 'read')
);

const hasHomeReadPermission = (permissions = {}, moduleName) => (
  hasReadPermission(permissions, moduleName)
  || hasReadPermission(permissions, MODULE_NAMES.HOME_DASHBOARD)
);

const createEmptyReports = () => ({
  accountBased: { groups: [], monthlyAchieved: [] },
  architect: { summary: {}, clients: [] },
  pm: { summary: {}, clients: [] },
});

const HomeEmptyState = () => {
  return (
    <div className="home-report-empty-state">
      <div className="home-report-empty-state-card">
        <img
          className="home-report-empty-state-icon"
          src={noDataSearchIcon}
          alt=""
        />
        <p>Select the filters above to see the results</p>
      </div>
    </div>
  );
};

const HomeDisabledExportButton = ({ title }) => (
  <Button
    aria-label={`Download ${title} Excel`}
    className="p-button-text home-download-btn home-download-btn-disabled"
    disabled
    icon={<img src={downloadIcon} alt="" />}
    tooltip="Download Excel"
    tooltipOptions={{ position: 'top' }}
  />
);

HomeDisabledExportButton.propTypes = {
  title: PropTypes.string.isRequired,
};

const HOME_TABLE_HEIGHT = '500px';
const HOME_ACCOUNT_CHART_HEIGHT = 300;
const ACCOUNT_BASED_CHART_BAR_COLOR = '#4a82e3';

const ACCOUNT_BASED_COLUMNS = [
  { field: 'groupName', header: ' ', visible: true, width: 170 },
  { field: 'name', header: 'Client Name', visible: true, width: 150 },
  { field: 'projectName', header: 'Project Name', visible: true, width: 180 },
  { field: 'targetAmount', header: 'Target (INR)', visible: true, width: 130 },
  { field: 'achievedAmount', header: 'Achieved (INR)', visible: true, width: 130 },
  { field: 'gmTarget', header: 'GM Target', visible: true, width: 110 },
  { field: 'agm', header: 'AGM (%)', visible: true, width: 100 },
  { field: 'egm', header: 'EGM (%)', visible: true, width: 100 },
];

const ARCHITECT_COLUMNS = [
  { field: 'clientName', header: 'Client Name', visible: true, width: 120, order: 1 },
  { field: 'projectName', header: 'Project Name', visible: true, width: 120, order: 2 },
  {
    field: 'revenueUnit',
    header: 'Revenue Unit',
    visible: true,
    width: 135,
    type: 'number',
    align: 'right',
    order: 3,
  },
  {
    field: 'technologyAuditRating',
    header: 'Technology Audit Rating',
    visible: true,
    order: 4,
    children: [
      {
        field: 'technologyTarget',
        header: 'Target',
        visible: true,
        width: 115,
        type: 'number',
        align: 'right',
        order: 5,
      },
      {
        field: 'technologyAchieved',
        header: 'Achieved',
        visible: true,
        width: 115,
        type: 'number',
        align: 'right',
        order: 6,
      },
    ],
  },
  {
    field: 'effortDeviation',
    header: 'Effort Deviation (%)',
    visible: true,
    order: 7,
    children: [
      {
        field: 'effortDeviationTarget',
        header: 'Target',
        visible: true,
        width: 115,
        type: 'number',
        align: 'right',
        order: 8,
      },
      {
        field: 'effortDeviationAchieved',
        header: 'Achieved',
        visible: true,
        width: 115,
        type: 'number',
        align: 'right',
        order: 9,
      },
    ],
  },
  {
    field: 'utilization',
    header: 'Utilization (%)',
    visible: true,
    order: 10,
    children: [
      {
        field: 'utilizationTarget',
        header: 'Target',
        visible: true,
        width: 115,
        type: 'number',
        align: 'right',
        order: 11,
      },
      {
        field: 'utilizationAchieved',
        header: 'Achieved',
        visible: true,
        width: 115,
        type: 'number',
        align: 'right',
        order: 12,
      },
    ],
  },
  { field: 'technologyTarget', header: 'Target', visible: false, order: 13 },
  { field: 'technologyAchieved', header: 'Achieved', visible: false, order: 14 },
  { field: 'effortDeviationTarget', header: 'Target', visible: false, order: 15 },
  { field: 'effortDeviationAchieved', header: 'Achieved', visible: false, order: 16 },
  { field: 'utilizationTarget', header: 'Target', visible: false, order: 17 },
  { field: 'utilizationAchieved', header: 'Achieved', visible: false, order: 18 },
];

const PM_COLUMNS = [
  { field: 'clientName', header: 'Client Name', visible: true, width: 110, order: 1 },
  { field: 'projectName', header: 'Project Name', visible: true, width: 110, order: 2 },
  {
    field: 'revenueUnit',
    header: 'Revenue Unit',
    visible: true,
    width: 120,
    type: 'number',
    align: 'right',
    order: 3,
  },
  {
    field: 'averageCsat',
    header: 'Average CSAT',
    visible: true,
    order: 4,
    children: [
      {
        field: 'csatTarget',
        header: 'Target',
        visible: true,
        width: 95,
        type: 'number',
        align: 'right',
        order: 5,
      },
      {
        field: 'csatAchieved',
        header: 'Achieved',
        visible: true,
        width: 95,
        type: 'number',
        align: 'right',
        order: 6,
      },
    ],
  },
  {
    field: 'csatReceived',
    header: 'CSAT Received (%)',
    visible: true,
    order: 7,
    children: [
      {
        field: 'csatReceivedTarget',
        header: 'Target',
        visible: true,
        width: 95,
        type: 'number',
        align: 'right',
        order: 8,
      },
      {
        field: 'csatReceivedAchieved',
        header: 'Achieved',
        visible: true,
        width: 95,
        type: 'number',
        align: 'right',
        order: 9,
      },
    ],
  },
  {
    field: 'qualityAuditRating',
    header: 'Quality Audit Rating',
    visible: true,
    order: 10,
    children: [
      {
        field: 'qualityAuditTarget',
        header: 'Target',
        visible: true,
        width: 95,
        type: 'number',
        align: 'right',
        order: 11,
      },
      {
        field: 'qualityAuditAchieved',
        header: 'Achieved',
        visible: true,
        width: 95,
        type: 'number',
        align: 'right',
        order: 12,
      },
    ],
  },
  {
    field: 'pmoAuditRating',
    header: 'PMO Audit Rating',
    visible: true,
    order: 13,
    children: [
      {
        field: 'pmoAuditTarget',
        header: 'Target',
        visible: true,
        width: 95,
        type: 'number',
        align: 'right',
        order: 14,
      },
      {
        field: 'pmoAuditAchieved',
        header: 'Achieved',
        visible: true,
        width: 95,
        type: 'number',
        align: 'right',
        order: 15,
      },
    ],
  },
  { field: 'csatTarget', header: 'Target', visible: false, order: 16 },
  { field: 'csatAchieved', header: 'Achieved', visible: false, order: 17 },
  { field: 'csatReceivedTarget', header: 'Target', visible: false, order: 18 },
  { field: 'csatReceivedAchieved', header: 'Achieved', visible: false, order: 19 },
  { field: 'qualityAuditTarget', header: 'Target', visible: false, order: 20 },
  { field: 'qualityAuditAchieved', header: 'Achieved', visible: false, order: 21 },
  { field: 'pmoAuditTarget', header: 'Target', visible: false, order: 22 },
  { field: 'pmoAuditAchieved', header: 'Achieved', visible: false, order: 23 },
];

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') {
    return value.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
    });
  }
  return value;
};

const formatAmountValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') {
    if (value === 0) return '0';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return value;
};

const hasRequiredHomeFilters = (selectedFilters = {}, requiredFields = []) => requiredFields.every((field) => {
  const value = selectedFilters[field];
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
});

const renderAccountText = (value) => (
  <span className="home-account-cell-text" title={value || '-'}>
    {value || '-'}
  </span>
);

const renderBlankAccountText = () => '\u00a0';

const renderAccountClientName = (value) => (
  <span className="home-account-cell-text" title={value || '-'}>
    {value || '-'}
  </span>
);

const renderReportClientName = (value, isExpanded) => [
  <span key="expand-marker" className="home-report-client-expand-marker">
    {isExpanded ? '-' : '+'}
  </span>,
  <span key="client-name" className="home-report-client-name">
    {value || ''}
  </span>,
];

const getUserInfoPayload = (userProfile = {}) => ({
  email: userProfile?.email || '',
  emailId: userProfile?.email || '',
  employeeId: userProfile?.employeeId || userProfile?.employee_id || userProfile?.emp_no || '',
  emp_id: userProfile?.emp_id || userProfile?.empId || userProfile?.internalEmployeeId || '',
  permissions: userProfile?.permissions || {},
  roleDetails: Array.isArray(userProfile?.roleDetails) ? userProfile.roleDetails : [],
});

const isSuperAdminUser = (userProfile = {}) => {
  const superAdminFlag = userProfile?.isSuperAdmin
    ?? userProfile?.superAdmin
    ?? userProfile?.super_admin;
  if (superAdminFlag === true || superAdminFlag === 1 || superAdminFlag === '1') return true;

  const roleDetails = Array.isArray(userProfile?.roleDetails)
    ? userProfile.roleDetails
    : userProfile?.roleDetails
      ? [userProfile.roleDetails]
      : [];

  return roleDetails.some((role) => {
    const roleName = String(role?.roleName || role?.role_name || role?.name || '').trim()
      .toLowerCase();
    const roleId = Number(role?.role_id ?? role?.roleId);
    return roleName === 'super admin' || roleId === 1;
  });
};

const getInitialHomeFilterValues = (defaultValues, hasAccess, ownValue) => {
  if (hasAccess && ownValue) return [ownValue];
  return Array.isArray(defaultValues) ? defaultValues : [];
};

const mapFilterOptions = (data = {}) => ({
  financialYear: data.financialYears || [],
  quarterId: (data.quarters || []).map((option) => ({ value: option.id, label: option.name })),
  seniorManagerEmpNo: data.seniorManagers || [],
  architectEmpNo: (data.architects || []).map((option) => ({
    value: option.value,
    label: option.label,
  })),
  pmEmpId: (data.projectManagers || []).map((option) => ({
    value: option.value,
    label: option.label,
  })),
});

const toTableUtilityOption = (option = {}) => {
  const value = String(option?.value ?? option?.id ?? '').trim();
  const label = String(option?.label ?? option?.name ?? value).trim();

  return {
    id: value,
    name: label,
    label,
    value,
  };
};

const buildHomeFilterColumn = (field = {}, options = [], selectedValue = []) => {
  const normalizedOptions = options.map(toTableUtilityOption).filter((option) => option.value);
  const isSingleSelectFilter = SINGLE_SELECT_FILTERS.has(field.key);
  const selectedArray = Array.isArray(selectedValue)
    ? selectedValue.map(String).filter(Boolean)
    : (selectedValue ? [String(selectedValue)] : []);
  const matchedSelectedOptions = normalizedOptions.filter(
    (option) => selectedArray.includes(String(option.value || '')),
  );

  return {
    field: field.key,
    header: field.label,
    width: 200,
    filter: true,
    visible: true,
    sortable: false,
    pinnable: false,
    order: field.order || 0,
    defaultPinned: false,
    isGlobalFilter: true,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
    menuItems: [{
      label: field.label,
      items: [
        {
          type: 'filter',
          field: field.key,
          label: field.label,
          placeholder: field.label,
          template: isSingleSelectFilter ? 'singleSelect' : 'multiSelect',
          selectedValue: isSingleSelectFilter ? (matchedSelectedOptions[0] || null) : null,
          selectedValues: isSingleSelectFilter ? matchedSelectedOptions.slice(0, 1) : matchedSelectedOptions,
          value: isSingleSelectFilter ? (selectedArray[0] || null) : null,
          options: normalizedOptions,
          values: normalizedOptions,
        },
      ],
    }],
  };
};

const extractAllFilterValues = (filterItem = {}) => {
  const selected = filterItem?.selectedValues
    ?? filterItem?.selectedValue
    ?? filterItem?.selected
    ?? filterItem?.value
    ?? null;

  if (Array.isArray(selected)) {
    return selected.map((item) => {
      if (!item) return '';
      if (typeof item === 'object') {
        return String(item.value ?? item.id ?? item.label ?? '').trim();
      }
      return String(item).trim();
    }).filter(Boolean);
  }

  if (!selected) return [];
  if (typeof selected === 'object') {
    const val = String(selected.value ?? selected.id ?? selected.label ?? '').trim();
    return val ? [val] : [];
  }
  const val = String(selected).trim();
  return val ? [val] : [];
};

const mapTableUtilityFiltersToHomeFilters = (appliedFilters = [], baseFilters = {}) => {
  const nextFilters = { ...baseFilters };

  appliedFilters.forEach((filterItem) => {
    if (!filterItem?.field) return;
    const field = filterItem.field;
    const vals = extractAllFilterValues(filterItem);
    if (field === 'financialYear') {
      nextFilters[field] = vals[0] || baseFilters[field] || '';
    } else {
      nextFilters[field] = vals;
    }
  });

  return nextFilters;
};

const buildHomeFilterHostConfiguration = ({ authToken, userInfo }) => ({
  generalSettings: {
    userInfo,
    headers: {
      token: authToken,
    },
  },
  tableSettings: {
    tableConfig: [],
  },
});

const createRowConfiguration = (backgroundColor, emphasizedFields = [], colorTemplates = []) => ({
  backgroundColor,
  template: [
    ...emphasizedFields.map((field) => ({ field, fontWeight: '700' })),
    ...colorTemplates,
  ],
});

const LEGACY_DASHBOARD_RED = '#eb5757';
const LEGACY_DASHBOARD_AMBER = '#ff8c06';
const LEGACY_DASHBOARD_GREEN = '#27ae60';

const toMetricNumber = (value) => {
  if (value === null || value === undefined || value === '' || value === '-' || value === 'NA') {
    return null;
  }

  const normalized = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(normalized) ? normalized : null;
};

const createMetricColorTemplate = (field, color) => (color ? { field, textColor: color } : null);

const formatColoredMetricValue = (value, getColor) => {
  const formattedValue = formatValue(value);
  const color = getColor(value);

  return color
    ? <span style={{ color }}>{formattedValue}</span>
    : formattedValue;
};

const formatLegacyMetricValue = (value, getColor) => {
  if (value === null || value === undefined || value === '' || value === '-') {
    return formatColoredMetricValue(0, getColor);
  }
  return formatColoredMetricValue(value, getColor);
};

const getTechnologyMetricColor = (value) => {
  const metricValue = toMetricNumber(value);
  if (metricValue === null) return null;
  if (metricValue > 8) return LEGACY_DASHBOARD_GREEN;
  if (metricValue >= 7) return LEGACY_DASHBOARD_AMBER;
  return LEGACY_DASHBOARD_RED;
};

const getEffortDeviationMetricColor = (value) => {
  const metricValue = toMetricNumber(value);
  if (metricValue === null) return null;
  if (metricValue < 20) return LEGACY_DASHBOARD_GREEN;
  if (metricValue > 20 && metricValue <= 50) return LEGACY_DASHBOARD_AMBER;
  return LEGACY_DASHBOARD_RED;
};

const getUtilizationMetricColor = (value) => {
  const metricValue = toMetricNumber(value);
  if (metricValue === null) return null;
  if (metricValue === 0) return null;
  if (metricValue > 90) return LEGACY_DASHBOARD_GREEN;
  if (metricValue >= 80) return LEGACY_DASHBOARD_AMBER;
  return LEGACY_DASHBOARD_RED;
};

const getCsatMetricColor = (value) => {
  const metricValue = toMetricNumber(value);
  if (metricValue === null) return null;
  if (metricValue > 4) return LEGACY_DASHBOARD_GREEN;
  if (metricValue > 3) return LEGACY_DASHBOARD_AMBER;
  return LEGACY_DASHBOARD_RED;
};

const getCsatReceivedMetricColor = (value) => {
  const metricValue = toMetricNumber(value);
  if (metricValue === null) return null;
  return metricValue >= 50 ? LEGACY_DASHBOARD_GREEN : LEGACY_DASHBOARD_RED;
};

const getAuditMetricColor = (value) => {
  const metricValue = toMetricNumber(value);
  if (metricValue === null) return null;
  if (metricValue > 8) return LEGACY_DASHBOARD_GREEN;
  if (metricValue >= 7) return LEGACY_DASHBOARD_AMBER;
  return LEGACY_DASHBOARD_RED;
};

const buildArchColorTemplates = (metrics = {}) => ([
  createMetricColorTemplate('technologyAchieved', getTechnologyMetricColor(metrics.technologyAchieved)),
  createMetricColorTemplate('effortDeviationAchieved', getEffortDeviationMetricColor(metrics.effortDeviationAchieved)),
  createMetricColorTemplate('utilizationAchieved', getUtilizationMetricColor(metrics.utilizationAchieved)),
].filter(Boolean));

const buildPmColorTemplates = (metrics = {}) => ([
  createMetricColorTemplate('csatAchieved', getCsatMetricColor(metrics.csatAchieved)),
  createMetricColorTemplate('csatReceivedAchieved', getCsatReceivedMetricColor(metrics.csatReceivedAchieved)),
  createMetricColorTemplate('qualityAuditAchieved', getAuditMetricColor(metrics.qualityAuditAchieved)),
  createMetricColorTemplate('pmoAuditAchieved', getAuditMetricColor(metrics.pmoAuditAchieved)),
].filter(Boolean));

const createEmptyAccountBasedChildRow = () => ({
  groupName: renderBlankAccountText(),
  name: renderAccountText('No client data available'),
  projectName: renderAccountText('-'),
  targetAmount: '-',
  achievedAmount: '-',
  gmTarget: '-',
  agm: '-',
  egm: '-',
  rowConfiguration: createRowConfiguration('#fcfdff', ['name']),
});

const createEmptyAccountBasedRow = () => ({
  groupName: renderBlankAccountText(),
  name: renderBlankAccountText(),
  projectName: renderAccountText('-'),
  targetAmount: '-',
  achievedAmount: '-',
  gmTarget: '-',
  agm: '-',
  egm: '-',
  rowConfiguration: createRowConfiguration('#f5f8ff', [
    'groupName',
  ]),
});

const mapAccountBasedRows = (groups = []) => {
  if (!groups || groups.length === 0) {
    return [createEmptyAccountBasedRow()];
  }

  return groups.map((group) => {
    const mappedClients = (group.clients || []).map((client) => {
      const projectRows = (client.projects || [])
        .filter((project) => project?.projectName)
        .map((project) => ({
          projectId: project.projectId,
          groupName: renderBlankAccountText(),
          name: renderBlankAccountText(),
          projectName: renderAccountText(project.projectName || '-'),
          targetAmount: '-',
          achievedAmount: formatAmountValue(project.invoiceAmount),
          gmTarget: '-',
          agm: formatValue(project.agm),
          egm: formatValue(project.egm),
          rowConfiguration: {
            ...createRowConfiguration('#ffffff'),
            template: [{ field: 'projectName', textColor: '#000000' }],
            onClick: [{ field: 'projectName' }],
          },
        }));

      return {
        groupName: renderBlankAccountText(),
        name: renderAccountClientName(client.clientName),
        projectName: renderAccountText('-'),
        targetAmount: formatAmountValue(client.targetAmount),
        achievedAmount: formatAmountValue(client.achievedAmount),
        gmTarget: '-',
        agm: '-',
        egm: '-',
        rowConfiguration: createRowConfiguration('#fcfdff', ['name']),
        ...(projectRows.length > 0 ? { children: projectRows } : {}),
      };
    });

    return {
      groupName: renderAccountText(group.targetName || '-'),
      name: renderBlankAccountText(),
      projectName: renderAccountText('-'),
      targetAmount: formatAmountValue(group.targetAmount),
      achievedAmount: formatAmountValue(group.achievedAmount),
      gmTarget: formatAmountValue(group.gmTarget),
      agm: formatValue(group.agm),
      egm: formatValue(group.egm),
      rowConfiguration: createRowConfiguration('#f5f8ff', [
        'groupName',
        'targetAmount',
        'achievedAmount',
      ]),
      children: mappedClients.length > 0
        ? mappedClients
        : [createEmptyAccountBasedChildRow()],
    };
  });
};

const buildAccountBasedMonthlyChart = (monthlyAchieved = []) => {
  const points = monthlyAchieved
    .filter((item) => item?.monthKey)
    .sort((left, right) => String(left.monthKey).localeCompare(String(right.monthKey)));

  const totalInr = points.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const labels = points.map((item) => (
    item.monthLabel
      ? item.monthLabel.replace(' ', '-')
      : item.monthKey
  ));
  const values = points.map((item) => Number(Number(item.amount || 0).toFixed(2)));
  const maxValue = Math.max(...values, 1);

  return {
    hasData: values.some((value) => value !== 0),
    totalInr,
    data: {
      labels,
      datasets: [
        {
          label: 'Achieved (INR)',
          data: values,
          backgroundColor: ACCOUNT_BASED_CHART_BAR_COLOR,
          barThickness: 56,
          maxBarThickness: 64,
          borderRadius: 2,
          borderSkipped: 'bottom',
          minBarLength: 4,
          borderColor: 'transparent',
          borderWidth: 1,
        },
      ],
    },
    options: {
      responsive: true,
      animation: {
        duration: 500,
        easing: 'easeInOutQuad',
      },
      'plugins.datalabels': {
        display: false,
        anchor: 'end',
        align: 'end',
        color: '#49454E',
        font: {
          family: 'Mukta',
          weight: 600,
          size: 12,
        },
      },
      'plugins.tooltip': {
        enabled: true,
        callbacks: {
          label: (context) => `Achieved: ${formatValue(context.raw)} INR`,
        },
      },
      'plugins.legend': {
        display: false,
      },
      'scales.x': {
        offset: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: '#7A757F',
          maxRotation: 0,
          minRotation: 0,
          font: {
            family: 'Mukta',
            weight: 500,
            size: 11,
          },
        },
        title: {
          display: true,
          text: 'Quarter in Month',
          color: '#49454E',
          font: {
            family: 'Mukta',
            weight: 600,
            size: 12,
          },
        },
      },
      'scales.y': {
        min: 0,
        suggestedMax: maxValue + Math.ceil(maxValue * 0.1),
        border: {
          display: false,
        },
        ticks: {
          display: true,
          color: '#7A757F',
          callback: (value) => formatValue(value),
          font: {
            family: 'Mukta',
            size: 12,
          },
        },
        grid: {
          color: '#e6ebf5',
          drawBorder: false,
        },
        title: {
          display: true,
          text: 'Achieved (INR)',
          color: '#49454E',
          font: {
            family: 'Mukta',
            weight: 600,
            size: 12,
          },
        },
      },
    },
  };
};

const mapArchitectRows = (data = {}) => {
  const summary = data.summary || {};
  const summaryRow = {
    id: 'architect-summary',
    clientName: '',
    projectName: '-',
    revenueUnit: formatValue(summary.revenueUnit),
    technologyTarget: formatValue(summary.technologyTarget),
    technologyAchieved: formatColoredMetricValue(summary.technologyAchieved, getTechnologyMetricColor),
    effortDeviationTarget: formatValue(summary.effortDeviationTarget),
    effortDeviationAchieved: formatColoredMetricValue(summary.effortDeviationAchieved, getEffortDeviationMetricColor),
    utilizationTarget: formatValue(summary.utilizationTarget),
    utilizationAchieved: formatColoredMetricValue(summary.utilizationAchieved, getUtilizationMetricColor),
    rowConfiguration: createRowConfiguration('#f5f8ff', [
      'revenueUnit',
      'technologyTarget',
      'technologyAchieved',
      'effortDeviationTarget',
      'effortDeviationAchieved',
      'utilizationTarget',
      'utilizationAchieved',
    ], buildArchColorTemplates(summary)),
  };

  const clientRows = (data.clients || []).map((client, clientIndex) => ({
    id: `architect-client-${clientIndex}`,
    clientName: client.clientName || '',
    projectName: '-',
    revenueUnit: '-',
    technologyTarget: '-',
    technologyAchieved: '-',
    effortDeviationTarget: '-',
    effortDeviationAchieved: '-',
    utilizationTarget: '-',
    utilizationAchieved: '-',
    rowConfiguration: createRowConfiguration('#fcfdff', ['clientName']),
    children: (client.projects || []).map((project, projectIndex) => ({
      id: `architect-project-${clientIndex}-${projectIndex}`,
      clientName: '',
      projectName: project.projectName || '-',
      projectId: project.projectId,
      revenueUnit: formatValue(project.revenueUnit),
      technologyTarget: '-',
      technologyAchieved: formatValue(project.technologyAchieved ?? 0),
      effortDeviationTarget: '-',
      effortDeviationAchieved: formatValue(project.effortDeviationAchieved),
      utilizationTarget: '-',
      utilizationAchieved: formatValue(project.utilizationAchieved),
      rowConfiguration: {
        ...createRowConfiguration('#ffffff', [], buildArchColorTemplates({
          technologyAchieved: project.technologyAchieved,
          effortDeviationAchieved: project.effortDeviationAchieved,
          utilizationAchieved: project.utilizationAchieved,
        })),
        onClick: [{ field: 'projectName' }],
      },
    })),
  }));

  return [summaryRow, ...clientRows];
};

const mapPmRows = (data = {}) => {
  const summary = data.summary || {};
  const summaryRow = {
    id: 'pm-summary',
    clientName: '',
    projectName: '-',
    revenueUnit: formatValue(summary.revenueUnit),
    csatTarget: formatValue(summary.csatTarget),
    csatAchieved: formatLegacyMetricValue(summary.csatAchieved, getCsatMetricColor),
    csatReceivedTarget: '-',
    csatReceivedAchieved: formatColoredMetricValue(summary.csatReceivedAchieved ?? 0, getCsatReceivedMetricColor),
    qualityAuditTarget: formatValue(summary.qualityAuditTarget),
    qualityAuditAchieved: formatLegacyMetricValue(summary.qualityAuditAchieved, getAuditMetricColor),
    pmoAuditTarget: formatValue(summary.pmoAuditTarget),
    pmoAuditAchieved: formatLegacyMetricValue(summary.pmoAuditAchieved, getAuditMetricColor),
    rowConfiguration: createRowConfiguration('#f5f8ff', [
      'revenueUnit',
      'csatTarget',
      'csatAchieved',
      'csatReceivedTarget',
      'csatReceivedAchieved',
      'qualityAuditTarget',
      'qualityAuditAchieved',
      'pmoAuditTarget',
      'pmoAuditAchieved',
    ], buildPmColorTemplates(summary)),
  };

  const clientRows = (data.clients || []).map((client, clientIndex) => ({
    id: `pm-client-${clientIndex}`,
    clientName: client.clientName || '-',
    projectName: '-',
    revenueUnit: '-',
    csatTarget: '-',
    csatAchieved: '-',
    csatReceivedTarget: '-',
    csatReceivedAchieved: '-',
    qualityAuditTarget: '-',
    qualityAuditAchieved: '-',
    pmoAuditTarget: '-',
    pmoAuditAchieved: '-',
    rowConfiguration: createRowConfiguration('#fcfdff', ['clientName']),
    children: (client.projects || []).map((project, projectIndex) => ({
      id: `pm-project-${clientIndex}-${projectIndex}`,
      clientName: '',
      projectName: project.projectName || '-',
      projectId: project.projectId,
      revenueUnit: formatValue(project.revenueUnit),
      csatTarget: '-',
      csatAchieved: formatLegacyMetricValue(project.latestCsat, getCsatMetricColor),
      csatReceivedTarget: '-',
      csatReceivedAchieved: '-',
      qualityAuditTarget: '-',
      qualityAuditAchieved: formatLegacyMetricValue(project.qualityAudit, getAuditMetricColor),
      pmoAuditTarget: '-',
      pmoAuditAchieved: formatLegacyMetricValue(project.pmoAudit, getAuditMetricColor),
      rowConfiguration: {
        ...createRowConfiguration('#ffffff', [], buildPmColorTemplates({
          csatAchieved: project.latestCsat,
          qualityAuditAchieved: project.qualityAudit,
          pmoAuditAchieved: project.pmoAudit,
        })),
        onClick: [{ field: 'projectName' }],
      },
    })),
  })).sort((left, right) => {
    if (left.clientName === '-' && right.clientName !== '-') return -1;
    if (left.clientName !== '-' && right.clientName === '-') return 1;
    return String(left.clientName).localeCompare(String(right.clientName));
  });

  return [summaryRow, ...clientRows];
};

const getReportClientKey = (reportType, row = {}, index = 0) => (
  `${reportType}-${row.clientName || 'client'}-${index}`
);

const getExpandableReportClientKeys = (rows = [], reportType) => rows.reduce((keys, row, index) => {
  if (Array.isArray(row.children) && row.children.length > 0) {
    keys.push(getReportClientKey(reportType, row, index));
  }
  return keys;
}, []);

const createReportCellStyle = (row, additionalTemplates = []) => ({
  ...(row.rowConfiguration || {}),
  template: [
    ...(row.rowConfiguration?.template || []),
    ...additionalTemplates,
  ],
});

const flattenClientProjectRows = (
  rows = [],
  expandedClients = {},
  reportType = 'report',
) => rows.flatMap((row, index) => {
  const children = Array.isArray(row.children) ? row.children : [];
  if (children.length === 0) return [row];

  const clientKey = getReportClientKey(reportType, row, index);
  const isExpanded = Boolean(expandedClients[clientKey]);
  const clientRow = {
    ...row,
    clientName: renderReportClientName(row.clientName, isExpanded),
    rowConfiguration: {
      ...createReportCellStyle(row),
      onClick: [{ field: 'clientName' }],
    },
    children: undefined,
    reportClientKey: clientKey,
  };

  if (!isExpanded) return [clientRow];

  return [
    clientRow,
    ...children.map((child) => ({
      ...child,
      clientName: ' ',
      rowConfiguration: {
        ...createReportCellStyle(child),
        backgroundColor: '#ffffff',
      },
    })),
  ];
});

const buildHomeReportConfiguration = ({
  authToken,
  columns,
  enableTreeTable = true,
  maxFrozenColumns = { desktop: 1, tablet: 1, mobile: 1 },
  onCellClick,
  onDownload,
  tableClassName = 'home-report-table-utility',
  title,
  userInfo,
}) => ({
  generalSettings: {
    enableApiFiltering: false,
    handleDataToCSV: onDownload,
    showToolBar: true,
    showPagination: false,
    headers: {
      token: authToken,
    },
    handler: {
      onCellClickHandler: onCellClick,
    },
    userInfo,
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: false },
      saveOrUpdateView: { endpoint: '', enabled: false },
      deleteView: { endpoint: '', enabled: false },
    },
    toolBarSettings: {
      showAddNew: false,
      showColumnFilterMenu: false,
      showColumnCustomize: false,
      showDownloadCsv: true,
      showDownloadExcel: false,
      showExpandCollapse: false,
      showHelp: false,
      showReset: false,
      showSaveView: false,
      showSearch: false,
      showToolBarHeading: true,
      toolBarHeading: title,
      additionalElements: [],
    },
  },
  apiEndpoints: {
    getAllViews: { endpoint: '', enabled: false },
    getFilteredData: { endpoint: '', enabled: false },
    saveOrUpdateView: { endpoint: '', enabled: false },
    deleteView: { endpoint: '', enabled: false },
  },
  tableSettings: {
    enableTreeTable,
    size: 'small',
    loaderSkeleton: { rows: 6, bodyCells: 1, columns: columns.length },
    pageSize: 100,
    showPageSizeSelector: false,
    paginationConfig: { page: 1, limit: 100 },
    showExpandCollapse: false,
    tableConfig: columns,
    tableScrollHeight: HOME_TABLE_HEIGHT,
    maxFrozenColumns,
    tableClassName,
  },
});

function Home() {
  const { userProfile } = useAuth();
  const { snackbarOpen } = useSnackbar();

  const userInfo = useMemo(() => getUserInfoPayload(userProfile), [userProfile]);
  const isSuperAdmin = isSuperAdminUser(userProfile);
  const hasAnyHomePermission = useMemo(() => HOME_PERMISSION_MODULES.some(
    (moduleName) => hasHomeReadPermission(userProfile?.permissions, moduleName),
  ) || isSuperAdmin, [isSuperAdmin, userProfile?.permissions]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedArchitectClients, setExpandedArchitectClients] = useState({});
  const [expandedPmClients, setExpandedPmClients] = useState({});
  const [areAllDashboardRowsExpanded, setAreAllDashboardRowsExpanded] = useState(false);
  const accountBasedTableRef = useRef(null);
  const [meta, setMeta] = useState({
    sections: {
      accountBasedReport: false,
      architectReport: false,
      pmReport: false,
    },
    filters: {
      seniorManager: false,
      architect: false,
      pm: false,
    },
  });
  const [filterOptions, setFilterOptions] = useState({
    financialYear: [],
    quarterId: [],
    seniorManagerEmpNo: [],
    architectEmpNo: [],
    pmEmpId: [],
  });
  const [filters, setFilters] = useState({
    financialYear: '',
    quarterId: [],
    seniorManagerEmpNo: [],
    architectEmpNo: [],
    pmEmpId: [],
  });
  const [defaultFilters, setDefaultFilters] = useState({
    financialYear: '',
    quarterId: [],
    seniorManagerEmpNo: [],
    architectEmpNo: [],
    pmEmpId: [],
  });
  const [reports, setReports] = useState(createEmptyReports());
  const filtersRef = useRef(filters);
  const filtersLoadedRef = useRef(false);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const openExportMenu = Boolean(anchorEl);

  const fetchDashboardData = useCallback(async (overrideFilters = null) => {
    const payload = {
      userInfo,
      ...(overrideFilters || filtersRef.current),
    };

    const response = await UseAxios(getHomeDashboardData(payload), setDashboardLoading);
    if (response?.data?.success) {
      setMeta((prev) => ({ ...prev, ...(response.data.data?.meta || {}) }));
      setReports(response.data.data?.reports || createEmptyReports());
      setExpandedArchitectClients({});
      setExpandedPmClients({});
      return;
    }

    snackbarOpen('Failed to fetch dashboard data', 'error');
  }, [snackbarOpen, userInfo]);

  useEffect(() => {
    let isMounted = true;

    if (filtersLoadedRef.current) return undefined;

    setPermissionsLoaded(false);

    if (!hasAnyHomePermission) {
      filtersLoadedRef.current = true;
      setPermissionsLoaded(true);
      return undefined;
    }

    const loadFilters = async () => {
      const response = await UseAxios(getHomeDashboardFilters(userInfo), setFiltersLoading);
      if (!isMounted) return;

      if (response?.data?.success) {
        const data = response.data.data || {};
        const nextDefaultFilters = data.defaultFilters || {};
        const sections = data.meta?.sections || {};
        const userEmpNo = userProfile?.employeeId
          || userProfile?.employee_id
          || userProfile?.emp_no
          || '';
        const ownPmOption = (data.projectManagers || []).find(
          (option) => String(option?.emp_no || '').trim() === String(userEmpNo).trim(),
        );
        let ownPmEmpId = userProfile?.emp_id
          || userProfile?.empId
          || userProfile?.internalEmployeeId
          || ownPmOption?.value
          || '';
        if (!isSuperAdmin && sections.pmReport && !nextDefaultFilters.pmEmpId?.length && !ownPmEmpId && userEmpNo) {
          const employeeResponse = await UseAxios(getEmployeeById(userEmpNo)).catch(() => null);
          ownPmEmpId = employeeResponse?.data?.data?.emp_id || '';
        }
        const initialFilters = {
          financialYear: nextDefaultFilters.financialYear || '',
          quarterId: Array.isArray(nextDefaultFilters.quarterId) ? nextDefaultFilters.quarterId : [],
          seniorManagerEmpNo: isSuperAdmin ? [] : getInitialHomeFilterValues(
            nextDefaultFilters.seniorManagerEmpNo,
            sections.accountBasedReport,
            userEmpNo,
          ),
          architectEmpNo: isSuperAdmin ? [] : getInitialHomeFilterValues(
            nextDefaultFilters.architectEmpNo,
            sections.architectReport,
            userEmpNo,
          ),
          pmEmpId: isSuperAdmin ? [] : getInitialHomeFilterValues(
            nextDefaultFilters.pmEmpId,
            sections.pmReport,
            userEmpNo,
          ),
        };
        filtersLoadedRef.current = true;
        setMeta((prev) => data.meta || prev);
        setPermissionsLoaded(true);
        setFilterOptions(mapFilterOptions(data));
        setDefaultFilters(initialFilters);
        setFilters(initialFilters);

        if (!isSuperAdmin && Object.values(sections).some(Boolean)) {
          await fetchDashboardData(initialFilters);
        }

        return;
      }

      snackbarOpen('Failed to fetch dashboard filters', 'error');
    };

    loadFilters();

    return () => {
      isMounted = false;
    };
  }, [fetchDashboardData, hasAnyHomePermission, isSuperAdmin, snackbarOpen, userInfo]);

  const exportCurrentView = useCallback(async (reportType) => {
    const response = await UseAxios(getHomeDashboardExportData({
      userInfo,
      ...filters,
      reportType,
    }), setExportLoading);

    if (!response?.data?.success) {
      snackbarOpen('Failed to export dashboard report', 'error');
      return;
    }

    const exportData = response.data.data || {};
    await exportToExcel({
      data: exportData.tableData || [],
      workBookTitle: `HomeDashboard-${reportType}`,
      workSheetTitle: `HomeDashboard-${reportType}`,
      columnsConfig: (exportData.tableConfiguration || []).map((column) => ({
        header: column.header,
        key: column.field,
        width: 24,
      })),
      borderAllCells: true,
      outerBorder: true,
    });
    snackbarOpen('Dashboard report exported successfully', 'success');
  }, [filters, snackbarOpen, userInfo]);

  const visibleFilters = useMemo(() => FILTER_FIELDS.filter((field) => {
    if (field.key === 'seniorManagerEmpNo') return meta.filters?.seniorManager !== false;
    if (field.key === 'architectEmpNo') return meta.filters?.architect !== false;
    if (field.key === 'pmEmpId') return meta.filters?.pm !== false;
    return true;
  }), [meta.filters]);

  // const openDownloadMenu = useCallback((event) => {
  //   setAnchorEl(event.currentTarget);
  // }, []);

  const closeDownloadMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleExportSelection = useCallback((reportType) => {
    closeDownloadMenu();
    exportCurrentView(reportType);
  }, [closeDownloadMenu, exportCurrentView]);

  const handleReportClientCellClick = useCallback(({ fieldPath, rowData }) => {
    if (fieldPath === 'projectName' && rowData?.projectId) {
      const projectDetailsUrl = `/delivery/project-details?projectId=${encodeURIComponent(String(rowData.projectId))}`;
      window.open(projectDetailsUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (fieldPath !== 'clientName' || !rowData?.reportClientKey) return;
    const setExpandedClients = rowData.reportClientKey.startsWith('pm-')
      ? setExpandedPmClients
      : setExpandedArchitectClients;

    setExpandedClients((prev) => ({
      ...prev,
      [rowData.reportClientKey]: !prev[rowData.reportClientKey],
    }));
  }, []);

  const handleGlobalFilterApply = useCallback(async (appliedFilters = []) => {
    const nextFilters = (appliedFilters && appliedFilters.length > 0)
      ? mapTableUtilityFiltersToHomeFilters(appliedFilters, defaultFilters)
      : { ...defaultFilters };

    setFilters(nextFilters);
    await fetchDashboardData(nextFilters);
  }, [defaultFilters, fetchDashboardData]);

  const homeFilterColumns = useMemo(() => visibleFilters.map((field, index) => (
    buildHomeFilterColumn(
      { ...field, order: index + 1 },
      filterOptions[field.key] || [],
      filters[field.key],
    )
  )), [filterOptions, filters, visibleFilters]);

  const homeFilterHostConfiguration = useMemo(() => buildHomeFilterHostConfiguration({
    authToken: userProfile?.access_token || '',
    userInfo: {
      emailId: userInfo.emailId,
      permissions: userInfo.permissions,
      roleDetails: userInfo.roleDetails,
      employeeId: userInfo.employeeId,
      globalColors: userProfile?.siteConfiguration?.global_colors,
    },
  }), [
    userInfo,
    userProfile?.access_token,
    userProfile?.siteConfiguration?.global_colors,
  ]);

  const homeFilterToolbarKey = useMemo(() => JSON.stringify({
    fields: visibleFilters.map((field) => field.key),
    filters,
    optionCounts: Object.fromEntries(
      Object.entries(filterOptions).map(([key, values]) => [key, values.length]),
    ),
  }), [filterOptions, filters, visibleFilters]);

  const homeFilterTables = useMemo(() => ([{
    key: 'home-dashboard-global-filter-toolbar',
    configuration: homeFilterHostConfiguration,
    data: [],
  }]), [homeFilterHostConfiguration]);

  const tableUserInfo = useMemo(() => ({
    emailId: userInfo.emailId,
    permissions: userInfo.permissions,
    roleDetails: userInfo.roleDetails,
    employeeId: userInfo.employeeId,
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [userInfo, userProfile?.siteConfiguration?.global_colors]);

  const accountBasedTableConfiguration = useMemo(() => buildHomeReportConfiguration({
    authToken: userProfile?.access_token || '',
    columns: ACCOUNT_BASED_COLUMNS,
    maxFrozenColumns: { desktop: 0, tablet: 0, mobile: 0 },
    onCellClick: handleReportClientCellClick,
    onDownload: () => exportCurrentView('account-based'),
    tableClassName: 'home-report-table-utility home-account-report-table',
    title: 'Account Based Report',
    userInfo: tableUserInfo,
  }), [exportCurrentView, handleReportClientCellClick, tableUserInfo, userProfile?.access_token]);

  const visibleArchitectColumns = useMemo(
    () => filterHomeColumnsByPermission(ARCHITECT_COLUMNS, meta.architectColumns),
    [meta.architectColumns],
  );

  const visiblePmColumns = useMemo(
    () => filterHomeColumnsByPermission(PM_COLUMNS, meta.pmColumns),
    [meta.pmColumns],
  );

  const architectTableConfiguration = useMemo(() => buildHomeReportConfiguration({
    authToken: userProfile?.access_token || '',
    columns: visibleArchitectColumns,
    enableTreeTable: false,
    onCellClick: handleReportClientCellClick,
    onDownload: () => exportCurrentView('architect'),
    tableClassName: 'home-report-table-utility home-architect-report-table',
    title: 'Architect Report',
    userInfo: tableUserInfo,
  }), [
    exportCurrentView,
    handleReportClientCellClick,
    tableUserInfo,
    userProfile?.access_token,
    visibleArchitectColumns,
  ]);

  const pmTableConfiguration = useMemo(() => buildHomeReportConfiguration({
    authToken: userProfile?.access_token || '',
    columns: visiblePmColumns,
    enableTreeTable: false,
    onCellClick: handleReportClientCellClick,
    onDownload: () => exportCurrentView('pm'),
    tableClassName: 'home-report-table-utility home-pm-report-table',
    title: 'PM Report',
    userInfo: tableUserInfo,
  }), [exportCurrentView, handleReportClientCellClick, tableUserInfo, userProfile?.access_token, visiblePmColumns]);

  const accountBasedRows = useMemo(
    () => mapAccountBasedRows(reports.accountBased?.groups || []),
    [reports.accountBased],
  );

  const hasDashboardReportData = Boolean(
    reports.accountBased?.groups?.length
    || reports.architect?.clients?.length
    || reports.pm?.clients?.length,
  );

  const canShowAccountBasedReport = hasRequiredHomeFilters(filters, [
    'seniorManagerEmpNo',
  ]);
  const canShowArchitectReport = hasRequiredHomeFilters(filters, [
    'architectEmpNo',
  ]);
  const canShowPmReport = hasRequiredHomeFilters(filters, [
    'pmEmpId',
  ]);

  const architectSourceRows = useMemo(
    () => mapArchitectRows(reports.architect || { summary: {}, clients: [] }),
    [reports.architect],
  );

  const architectRows = useMemo(
    () => flattenClientProjectRows(architectSourceRows, expandedArchitectClients, 'architect'),
    [architectSourceRows, expandedArchitectClients],
  );

  const pmSourceRows = useMemo(
    () => mapPmRows(reports.pm || { summary: {}, clients: [] }),
    [reports.pm],
  );

  const pmRows = useMemo(
    () => flattenClientProjectRows(pmSourceRows, expandedPmClients, 'pm'),
    [pmSourceRows, expandedPmClients],
  );

  const handleToggleAllDashboardRows = useCallback(() => {
    setAreAllDashboardRowsExpanded((previous) => {
      const nextState = !previous;
      const updateExpandedClients = (rows, reportType, setter) => {
        const keys = getExpandableReportClientKeys(rows, reportType);
        setter(nextState ? Object.fromEntries(keys.map((key) => [key, true])) : {});
      };

      updateExpandedClients(architectSourceRows, 'architect', setExpandedArchitectClients);
      updateExpandedClients(pmSourceRows, 'pm', setExpandedPmClients);
      return nextState;
    });
  }, [architectSourceRows, pmSourceRows]);

  useEffect(() => {
    const tableHost = accountBasedTableRef.current;
    if (!tableHost) return undefined;

    let animationFrame = null;
    const syncTreeRows = () => {
      const togglers = [...tableHost.querySelectorAll('.p-treetable-toggler')];
      const visibleTogglers = togglers.filter((toggler) => {
        if (toggler.style.visibility === 'hidden') return false;
        const row = toggler.closest('tr');
        return row;
      });
      const rowsToToggle = visibleTogglers
        .map((toggler) => ({
          row: toggler.closest('tr'),
          toggler,
        }))
        .filter(({ row }) => (
          row.getAttribute('aria-expanded') !== String(areAllDashboardRowsExpanded)
        ));

      if (rowsToToggle.length === 0) return;

      const targetLevel = areAllDashboardRowsExpanded
        ? Math.min(...rowsToToggle.map(({ row }) => Number(row.getAttribute('aria-level'))))
        : Math.max(...rowsToToggle.map(({ row }) => Number(row.getAttribute('aria-level'))));
      const togglersToClick = rowsToToggle
        .filter(({ row }) => Number(row.getAttribute('aria-level')) === targetLevel)
        .map(({ toggler }) => toggler);

      togglersToClick.forEach((toggler) => toggler.click());
      animationFrame = window.requestAnimationFrame(syncTreeRows);
    };

    animationFrame = window.requestAnimationFrame(syncTreeRows);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [accountBasedRows, areAllDashboardRowsExpanded, canShowAccountBasedReport, meta.sections?.accountBasedReport]);

  const accountBasedMonthlyChart = useMemo(
    () => buildAccountBasedMonthlyChart(reports.accountBased?.monthlyAchieved || []),
    [reports.accountBased],
  );

  const shouldShowAccountBasedChart = canShowAccountBasedReport
    && accountBasedMonthlyChart.hasData;

  const hasHomeDashboardAccess = Object.values(meta.sections || {}).some(Boolean)
    || Object.values(meta.filters || {}).some(Boolean);

  useEffect(() => {
    const previousBodyOverflowY = document.body.style.overflowY;
    document.body.style.overflowY = permissionsLoaded && !hasHomeDashboardAccess ? 'hidden' : 'auto';

    return () => {
      document.body.style.overflowY = previousBodyOverflowY;
    };
  }, [hasHomeDashboardAccess, permissionsLoaded]);

  if (!permissionsLoaded) {
    return (
      <div className="home-dashboard-outer-layer home-permissions-loading">
        <Loader text={filtersLoading ? 'Loading dashboard filters' : ''} />
      </div>
    );
  }

  if (permissionsLoaded && !hasHomeDashboardAccess) {
    return (
      <div className="home-dashboard-outer-layer home-no-access">
        <div className="home-no-access-message">Welcome to Darpan</div>
      </div>
    );
  }

  return (
    <div className="home-dashboard-outer-layer">
      {dashboardLoading ? (
        <div className="loader" role="status" aria-live="polite">
          <Loader text="Refreshing dashboard" />
        </div>
      ) : (
        <div className="home-dashboard-content">
          <h1 className="home-dashboard-welcome">Welcome to Darpan</h1>
          <div className="home-filter-toolbar">
            <div className="home-filter-toolbar-utility">
              <TableUtility
                key={homeFilterToolbarKey}
                tables={homeFilterTables}
                globalFilterConfig={homeFilterColumns}
                showGlobalFilter
                showTieredMenu
                onGlobalFilterApply={handleGlobalFilterApply}
                emptyStateComponent={<div className="home-filter-toolbar-empty" />}
                renderGlobalFilterTrigger={(globalFilterTrigger) => (
                  <div className="home-filter-toolbar-layout">
                    <div className="home-filter-toolbar-label">Dashboard</div>
                    <div className="home-filter-toolbar-actions">
                      <div className="home-filter-global-trigger">
                        {globalFilterTrigger}
                      </div>
                      <Tooltip title={areAllDashboardRowsExpanded ? 'Collapse all' : 'Expand all'}>
                        <IconButton
                          aria-label={areAllDashboardRowsExpanded
                            ? 'Collapse all dashboard rows'
                            : 'Expand all dashboard rows'}
                          className="home-expand-collapse-btn"
                          disabled={!hasDashboardReportData}
                          onClick={handleToggleAllDashboardRows}
                        >
                          <img
                            src={areAllDashboardRowsExpanded ? collapseAllIcon : expandAllIcon}
                            alt=""
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>

          <Menu
            anchorEl={anchorEl}
            open={openExportMenu}
            onClose={closeDownloadMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() => handleExportSelection('all')}
              disabled={exportLoading}
            >
            Download all
            </MenuItem>
            <MenuItem
              onClick={() => handleExportSelection('account-based')}
              disabled={exportLoading}
            >
            Account Based Report
            </MenuItem>
            <MenuItem
              onClick={() => handleExportSelection('architect')}
              disabled={exportLoading}
            >
            Architect Report
            </MenuItem>
            <MenuItem
              onClick={() => handleExportSelection('pm')}
              disabled={exportLoading}
            >
            PM Report
            </MenuItem>
          </Menu>

          {meta.sections?.accountBasedReport !== false && (
            <section className={`home-section-card${!canShowAccountBasedReport ? ' home-section-card-empty' : ''}`}>
              {!canShowAccountBasedReport && (
                <div className="home-report-section-header">
                  <h3 className="home-report-section-title">Account Based Report</h3>
                  <HomeDisabledExportButton title="Account Based Report" />
                </div>
              )}
              <div ref={accountBasedTableRef} className="home-dashboard-table-utility home-account-report-table-host">
                <TableUtility
                  tables={[{
                    configuration: accountBasedTableConfiguration,
                    data: canShowAccountBasedReport ? accountBasedRows : [],
                    key: 'account-based-report',
                  }]}
                  emptyStateComponent={!canShowAccountBasedReport ? <HomeEmptyState /> : undefined}
                />
              </div>

              {shouldShowAccountBasedChart && (
                <div className="home-account-based-chart-section">
                  <h3 className="home-account-based-chart-heading">Business Unit Revenue</h3>
                  <div className="home-account-based-chart-wrapper">
                    <Chart
                      chartType="bar"
                      data={accountBasedMonthlyChart.data}
                      customOptions={accountBasedMonthlyChart.options}
                      containerHeight={HOME_ACCOUNT_CHART_HEIGHT}
                      containerClass="home-account-based-chart"
                    />
                  </div>
                </div>
              )}
            </section>
          )}

          {meta.sections?.architectReport !== false && (
            <section className={`home-section-card${!canShowArchitectReport ? ' home-section-card-empty' : ''}`}>
              {!canShowArchitectReport && (
                <div className="home-report-section-header">
                  <h3 className="home-report-section-title">Architect Report</h3>
                  <HomeDisabledExportButton title="Architect Report" />
                </div>
              )}
              <div className="home-dashboard-table-utility">
                <TableUtility
                  tables={[{
                    configuration: architectTableConfiguration,
                    data: canShowArchitectReport ? architectRows : [],
                    key: 'architect-report',
                    useCustomBodyRenderer: true,
                  }]}
                  emptyStateComponent={!canShowArchitectReport ? <HomeEmptyState /> : undefined}
                />
              </div>
            </section>
          )}

          {meta.sections?.pmReport !== false && (
            <section className={`home-section-card${!canShowPmReport ? ' home-section-card-empty' : ''}`}>
              {!canShowPmReport && (
                <div className="home-report-section-header">
                  <h3 className="home-report-section-title">PM Report</h3>
                  <HomeDisabledExportButton title="PM Report" />
                </div>
              )}
              <div className="home-dashboard-table-utility">
                <TableUtility
                  tables={[{
                    configuration: pmTableConfiguration,
                    data: canShowPmReport ? pmRows : [],
                    key: 'pm-report',
                    useCustomBodyRenderer: true,
                  }]}
                  emptyStateComponent={!canShowPmReport ? <HomeEmptyState /> : undefined}
                />
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

export default Home;
